import React, { useState } from 'react';
import { Button, Upload, message, Space } from 'antd';
import { FileExcelOutlined, UploadOutlined } from '@ant-design/icons';
import {
  downloadDetailTemplate,
  parseDetailExcelFile,
  buildDetailItemsFromExcel,
} from '../../utils/excelDetailTemplate';

/**
 * Download an Excel template + upload a filled template for a detail table.
 *
 * Props:
 * - type: 'correctionstock' | 'demarque' | 'reception' | 'inventaire'
 * - onImport: (items) => void | Promise<void> — receives resolved detail rows
 * - disabled: disables the upload button (e.g. when the document is validated)
 */
const ExcelDetailImport = ({ type, onImport, disabled = false }) => {
  const [importing, setImporting] = useState(false);

  const handleFile = async (file) => {
    setImporting(true);
    try {
      const rows = await parseDetailExcelFile(file);
      if (rows.length === 0) {
        message.warning('Aucune ligne avec CODE_ART trouvée dans le fichier');
        return false;
      }

      const { items, unknownCodes } = await buildDetailItemsFromExcel(rows, type);

      if (unknownCodes.length > 0) {
        const shown = unknownCodes.slice(0, 10).join(', ');
        const suffix = unknownCodes.length > 10 ? `... (+${unknownCodes.length - 10})` : '';
        message.warning(
          `${unknownCodes.length} code(s) article introuvable(s), ignoré(s): ${shown}${suffix}`,
          8
        );
      }

      if (items.length === 0) {
        message.error('Aucun article valide à importer');
        return false;
      }

      await onImport(items);
    } catch (e) {
      message.error(`Erreur lors de l'import Excel: ${e?.message || e}`);
    } finally {
      setImporting(false);
    }
    return false; // prevent antd from uploading the file anywhere
  };

  return (
    <Space>
      <Button
        icon={<FileExcelOutlined />}
        onClick={() => downloadDetailTemplate(type)}
      >
        Modèle Excel
      </Button>
      <Upload
        beforeUpload={handleFile}
        showUploadList={false}
        accept=".xlsx,.xls"
        maxCount={1}
        disabled={disabled || importing}
      >
        <Button
          icon={<UploadOutlined />}
          loading={importing}
          disabled={disabled}
        >
          Importer Excel
        </Button>
      </Upload>
    </Space>
  );
};

export default ExcelDetailImport;
